/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Input, Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationService } from '@alfresco/adf-core';
import { DateInputComponent, PageHeaderComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { AdminService, AuditApp, AuditEntry } from './admin.service';
import { downloadXlsx } from './xlsx-export';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-audit-log',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    PageHeaderComponent,
    DateInputComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './audit-log.component.html',
  styleUrl: './audit-log.component.scss'
})
export class AuditLogComponent implements OnInit {
  /**
   * Whether the screen is shown inside the settings page rather than on a
   * route of its own — it then drops its own title, because the page it
   * sits in already names it.
   */
  @Input() embedded = false;

  private readonly translate = inject(TranslateService);
  private readonly admin = inject(AdminService);
  private readonly notification = inject(NotificationService);

  private readonly datePipe = inject(DatePipe);

  apps: AuditApp[] = [];
  activeApp = '';
  entries: AuditEntry[] = [];
  loading = true;

  // filters — the dates go to the server, the text searches the page on screen
  searchText = '';
  dateFrom = '';
  dateTo = '';

  /** Entries come a page at a time, so the log is not cut off at its newest 100. */
  readonly pageSize = 100;
  pageIndex = 0;
  hasMore = false;

  /** The entry the detail panel is showing. */
  selected: AuditEntry | null = null;

  ngOnInit(): void {
    this.loadApps();
  }

  select(entry: AuditEntry | null): void {
    this.selected = entry;
  }

  /**
   * True when access auditing is not switched on and the page has nothing to
   * show — the banner then explains how to turn it on. With alfresco-access
   * enabled an empty page is just an empty result, not a missing setting.
   */
  get auditProducesNothing(): boolean {
    return !this.loading && !this.entries.length && !this.dateFrom && !this.dateTo && this.pageIndex === 0 && !this.accessAuditEnabled;
  }

  /** The application that records who opened, changed or deleted what. */
  private get accessAuditEnabled(): boolean {
    return this.apps.some((app) => app.id === AuditLogComponent.ACCESS_APP && app.isEnabled);
  }

  private static readonly ACCESS_APP = 'alfresco-access';

  /** 1-based position of the first and last row on this page. */
  get pageRange(): { from: number; to: number } {
    const from = this.entries.length ? this.pageIndex * this.pageSize + 1 : 0;
    return { from, to: this.pageIndex * this.pageSize + this.entries.length };
  }

  /**
   * The date range as the audit API's `where` clause. BETWEEN needs both ends,
   * so an open end is the epoch or now.
   */
  whereClause(): string | undefined {
    if (!this.dateFrom && !this.dateTo) {
      return undefined;
    }
    const from = this.dateFrom ? new Date(this.dateFrom + 'T00:00:00') : new Date(0);
    const to = this.dateTo ? new Date(this.dateTo + 'T23:59:59.999') : new Date();
    return `(createdAt BETWEEN ('${from.toISOString()}' , '${to.toISOString()}'))`;
  }

  /** A date changed — ask the server again, from the first page. */
  datesChanged(): void {
    this.pageIndex = 0;
    this.loadPage();
  }

  nextPage(): void {
    if (this.hasMore) {
      this.pageIndex++;
      this.loadPage();
    }
  }

  previousPage(): void {
    if (this.pageIndex > 0) {
      this.pageIndex--;
      this.loadPage();
    }
  }

  get enabledApps(): AuditApp[] {
    return this.apps.filter((app) => app.isEnabled);
  }

  /** The value map of an entry, flattened for display. */
  valueRows(entry: AuditEntry | null): { key: string; value: string }[] {
    const values = entry?.values;
    if (!values || typeof values !== 'object') {
      return [];
    }
    return Object.keys(values).map((key) => ({ key, value: this.stringify(values[key]) }));
  }

  private stringify(value: any): string {
    if (value === null || value === undefined) {
      return '—';
    }
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }

  metadataJson(entry: AuditEntry | null): string {
    return entry ? JSON.stringify(entry, null, 2) : '';
  }

  actorOf(entry: AuditEntry): string {
    return entry.createdByUser?.displayName || entry.createdByUser?.id || '—';
  }

  initial(name?: string): string {
    return (name || '?').trim().charAt(0).toUpperCase();
  }

  appName(id: string): string {
    return this.apps.find((app) => app.id === id)?.name || id;
  }

  trackById(_index: number, entry: AuditEntry): number {
    return entry.id;
  }

  /** The page's entries that match the search text — the dates were applied by the server. */
  filteredEntries(): AuditEntry[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) {
      return this.entries;
    }
    return this.entries.filter((e) => `${e.createdByUser?.id || ''} ${this.summarise(e)}`.toLowerCase().includes(q));
  }

  clearFilters(): void {
    const datesSet = !!(this.dateFrom || this.dateTo);
    this.searchText = '';
    this.dateFrom = '';
    this.dateTo = '';
    if (datesSet) {
      this.datesChanged();
    }
  }

  /** Exports the filtered rows as a real .xlsx workbook (supports Thai). */
  exportExcel(): void {
    const rows = this.filteredEntries();
    if (!rows.length) {
      return;
    }
    const headers = ['DMS.UI.CEE128', 'DMS.UI.A6CC55', 'DMS.UI.093FDD', 'DMS.UI.412A94'].map((k) => this.translate.instant(k));
    const data = rows.map((e) => [
      this.datePipe.transform(e.createdAt, 'yyyy-MM-dd HH:mm:ss') || '',
      e.createdByUser?.id || '',
      this.activeApp,
      this.summarise(e)
    ]);
    const stamp = this.datePipe.transform(new Date(), 'yyyyMMdd-HHmmss');
    downloadXlsx(`audit-${this.activeApp}-${stamp}.xlsx`, 'Audit', headers, data);
    this.notification.showInfo(this.translate.instant('DMS.UI.F5F114', { p0: rows.length }));
  }

  private loadApps(): void {
    this.loading = true;
    this.admin.listAuditApps().subscribe({
      next: (res) => {
        this.apps = (res?.list?.entries || []).map((e: any) => e.entry);
        // the access log is what this page is for; tagging is a side channel
        const firstEnabled = this.apps.find((a) => a.isEnabled && a.id === AuditLogComponent.ACCESS_APP) ?? this.apps.find((a) => a.isEnabled);
        if (firstEnabled) {
          this.selectApp(firstEnabled.id);
        } else {
          this.loading = false;
        }
      },
      error: () => {
        this.loading = false;
        this.apps = [];
      }
    });
  }

  selectApp(id: string): void {
    this.activeApp = id;
    this.pageIndex = 0;
    this.loadPage();
  }

  private loadPage(): void {
    this.loading = true;
    this.entries = [];
    this.selected = null;
    this.admin.listAuditEntries(this.activeApp, this.pageIndex * this.pageSize, this.pageSize, this.whereClause()).subscribe({
      next: (res) => {
        this.entries = (res?.list?.entries || []).map((e: any) => e.entry);
        this.hasMore = !!res?.list?.pagination?.hasMoreItems;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notification.showError(this.translate.instant('DMS.UI.CB4A40'));
      }
    });
  }

  reload(): void {
    if (this.activeApp) {
      this.loadPage();
    } else {
      this.loadApps();
    }
  }

  summarise(e: AuditEntry): string {
    const v = e?.values;
    if (!v) {
      return '—';
    }
    try {
      const parts = Object.keys(v)
        .slice(0, 4)
        .map((k) => `${k.split('/').pop()}: ${typeof v[k] === 'object' ? JSON.stringify(v[k]) : v[k]}`);
      return parts.join(' · ');
    } catch {
      return '—';
    }
  }
}
